import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './useAppStore';
import {
  fetchConversations,
  startNewConversation,
  loadConversation,
  sendMessage,
  clearChatError,
  clearCurrentConversation,
  addOptimisticMessage,
} from '../store/slices';

export const useChat = () => {
  const dispatch = useAppDispatch();
  const { conversations, currentConversation, isLoading, isSending, error } = useAppSelector(
    (state) => state.chat
  );

  const handleFetchConversations = useCallback(() => {
    return dispatch(fetchConversations()).unwrap();
  }, [dispatch]);

  const handleStartNewConversation = useCallback(() => {
    return dispatch(startNewConversation()).unwrap();
  }, [dispatch]);

  const handleLoadConversation = useCallback(
    (conversationId: string) => {
      return dispatch(loadConversation(conversationId)).unwrap();
    },
    [dispatch]
  );

  const handleSendMessage = useCallback(
    async (conversationId: string, message: string) => {
      // Add optimistic message
      dispatch(
        addOptimisticMessage({
          role: 'user',
          content: message,
          timestamp: new Date().toISOString(),
        })
      );
      return dispatch(sendMessage({ conversationId, message })).unwrap();
    },
    [dispatch]
  );

  const handleClearError = useCallback(() => {
    dispatch(clearChatError());
  }, [dispatch]);

  const handleClearCurrentConversation = useCallback(() => {
    dispatch(clearCurrentConversation());
  }, [dispatch]);

  return {
    conversations,
    currentConversation,
    isLoading,
    isSending,
    error,
    fetchConversations: handleFetchConversations,
    startNewConversation: handleStartNewConversation,
    loadConversation: handleLoadConversation,
    sendMessage: handleSendMessage,
    clearError: handleClearError,
    clearCurrentConversation: handleClearCurrentConversation,
  };
};
